import { bookingFailureKind } from "../booking/attempt.js";
import { paymentMethodLabel } from "../booking/methods.js";
import { ActionConfirmation, usePublishActionOutcome, type ActionOutcome } from "../ui/ActionConfirmation.js";
import { useEffect, useRef, useState } from "react";
import { idempotencyKey } from "../booking/api.js";
import {
  diaryManualCreditUseAllowed,
  diaryManualCreditUseMessages,
  useExistingLessonCredit,
  type DiaryPaymentClassification,
} from "../booking/diaryPayments.js";
import {
  paymentOperationAttempt,
  type PaymentOperationAttempt,
} from "../booking/paymentOperationModel.js";
import { staffBooking, type OnSitePaymentMethod } from "../booking/staff.js";
import { measureAction } from "../telemetry/actionTiming.js";

interface DiaryPaymentAttempt {
  method: OnSitePaymentMethod;
  paymentId: number;
  amount: string;
  paidAt: string;
  operation: PaymentOperationAttempt;
}

/**
 * Mark-paid / apply-credit, shared by TimeGrid's day/week lesson menu and the
 * list/month appointment drawer. The host only opens this for a single matched
 * student with an authoritative pending on-site classification; this component
 * keeps the money-recording checks local because applying an existing credit
 * does not require a pending payment id.
 */
export function PaymentPanel({
  appointmentId,
  studentId,
  payment,
  onBusyChange,
  onChanged,
  moneyOnly = false,
  lessonDescription,
  layout = "panel",
}: {
  lessonDescription?: string;
  appointmentId: number;
  studentId: string;
  payment: DiaryPaymentClassification;
  /** Lets a host that can also be closed (e.g. a drawer's Escape/backdrop) block that while a mutation is in flight. */
  onBusyChange?: (busy: boolean) => void;
  onChanged: () => void;
  moneyOnly?: boolean;
  layout?: "panel" | "inline";
}) {
  const publish = usePublishActionOutcome();
  const [outcome, setOutcome] = useState<ActionOutcome | null>(null);
  const [uncertain, setUncertain] = useState(false);
  function complete(result: ActionOutcome) { if (!publish(result)) setOutcome(result); onChanged(); }
  const [blocks, setBlocks] = useState<Awaited<ReturnType<typeof staffBooking.lessonCredits>>["blocks"] | null>(null);
  const [selectedBlock, setSelectedBlock] = useState("");
  const [manualUseMessages, setManualUseMessages] = useState<string[]>([]);
  const [hasCreditEntitlement, setHasCreditEntitlement] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [attempt, setAttempt] = useState<DiaryPaymentAttempt | null>(null);
  const creditAttempt = useRef<PaymentOperationAttempt | null>(null);
  const mutationInFlight = useRef(false);

  function setPaymentBusy(nextBusy: boolean) {
    mutationInFlight.current = nextBusy;
    setBusy(nextBusy);
    onBusyChange?.(nextBusy);
  }

  useEffect(() => {
    if (moneyOnly) return;
    let current = true;
    void staffBooking.lessonCredits(studentId)
      .then((summary) => {
        if (!current) return;
        const entitlement = summary.blocks.filter((block) => block.available_credits > 0);
        const manuallyUsable = entitlement.filter(diaryManualCreditUseAllowed);
        setHasCreditEntitlement(entitlement.length > 0);
        setBlocks(manuallyUsable);
        setManualUseMessages(diaryManualCreditUseMessages(entitlement));
        setSelectedBlock(manuallyUsable[0]?.lesson_block_id ?? "");
      })
      .catch((cause: unknown) => {
        if (current) setProblem(cause instanceof Error ? cause.message : "Credits could not be loaded.");
      });
    return () => { current = false; };
  }, [studentId, moneyOnly]);

  async function mark(method: OnSitePaymentMethod) {
    const paymentId = payment.payment_id;
    const amount = payment.amount_due;
    if (mutationInFlight.current || busy || paymentId === null || amount === null) return;

    // A timeout or 503 leaves the outcome uncertain. Retrying the same visible
    // payment action must therefore keep both its paid-at timestamp and its
    // idempotency key. Changing method/payment/amount starts a new operation.
    const sameIntent =
      attempt !== null &&
      attempt.method === method &&
      attempt.paymentId === paymentId &&
      attempt.amount === amount;
    const paidAt = sameIntent ? attempt.paidAt : new Date().toISOString();
    const input = {
      portal_student_id: studentId,
      amount,
      payment_method: method,
      paid_at: paidAt,
    };
    const operation = paymentOperationAttempt(
      sameIntent ? attempt.operation : null,
      "payment_received",
      input,
      idempotencyKey,
    );
    setAttempt({ method, paymentId, amount, paidAt, operation });

    setPaymentBusy(true);
    setProblem(null);
    try {
      await measureAction("payment.mark", () =>
        staffBooking.markPaymentReceived(
          appointmentId,
          paymentId,
          input,
          operation.key,
        ),
      );
      complete({ variant: "success", title: "Payment recorded", description: lessonDescription ?? `Lesson appointment #${appointmentId}`,
        details: [{ value: `£${amount} · ${paymentMethodLabel(method)}` }, { value: "This records payment already received. The Hub did not process a card payment." }] });
    } catch (cause) {
      setUncertain(bookingFailureKind(cause) === "uncertain");
      setProblem(
        cause instanceof Error
          ? cause.message
          : "The payment could not be confirmed. Retry this same payment before starting another.",
      );
    } finally {
      setPaymentBusy(false);
    }
  }

  async function applyCredit() {
    if (moneyOnly) return;
    if (mutationInFlight.current || busy || selectedBlock === "") return;
    const input = {
      amelia_appointment_id: appointmentId,
      portal_student_id: studentId,
      notes: "Applied from diary appointment actions",
    };
    // A lost response may follow a committed debit. Keep the same reference
    // when staff retry the same block/student/lesson allocation.
    const operation = paymentOperationAttempt(
      creditAttempt.current,
      "lesson_credit_use",
      { lesson_block_id: selectedBlock, ...input },
      idempotencyKey,
    );
    creditAttempt.current = operation;
    setPaymentBusy(true);
    setProblem(null);
    try {
      await measureAction("payment.credit", () =>
        useExistingLessonCredit(selectedBlock, {
          ...input,
          idempotency_key: operation.key,
        }),
      );
      let balance: number | null = null;
      let refreshWarning: string | null = null;
      try {
        const refreshed = await staffBooking.lessonCredits(studentId);
        if (refreshed.portal_student_id !== studentId || !Number.isFinite(refreshed.total_available_credits)) throw new Error("The refreshed credits did not match this student.");
        balance = refreshed.total_available_credits;
      } catch {
        refreshWarning = "The credit was applied, but the current balance could not be refreshed. Check credits before applying another; do not repeat this allocation.";
      }
      complete({ variant: refreshWarning ? "attention" : "success", title: "Lesson credit applied", description: lessonDescription ?? `Lesson appointment #${appointmentId}`,
        details: [...(balance !== null ? [{ label: "Remaining balance", value: `${balance} credits` }] : []), ...(refreshWarning ? [{ value: refreshWarning }] : [])] });
    } catch (cause) {
      setUncertain(bookingFailureKind(cause) === "uncertain");
      setProblem(cause instanceof Error ? cause.message : "Credit could not be applied.");
    } finally {
      setPaymentBusy(false);
    }
  }

  if (outcome) return <ActionConfirmation {...outcome} reveal />;

  if (layout === "inline") {
    const singleCreditBlock = blocks?.length === 1 ? blocks[0] ?? null : null;
    return (
      <div className="cal-payment-inline">
        <div className="cal-payment-actions cal-payment-actions-inline">
          {payment.payment_id !== null && payment.amount_due !== null && (
            <>
              <button type="button" aria-label="Paid cash" disabled={busy} onClick={() => void mark("cash")}>Cash</button>
              <button type="button" aria-label="Paid by card machine" disabled={busy} onClick={() => void mark("card_terminal")}>Card</button>
              <button type="button" aria-label="Paid by bank transfer" disabled={busy} onClick={() => void mark("bank_transfer")}>Bank</button>
            </>
          )}
          {!moneyOnly && singleCreditBlock !== null && (
            <button
              type="button"
              disabled={busy || selectedBlock !== singleCreditBlock.lesson_block_id}
              onClick={() => void applyCredit()}
            >
              Use credit ({singleCreditBlock.available_credits} left)
            </button>
          )}
        </div>

        {!moneyOnly && blocks !== null && blocks.length > 1 && (
          <div className="cal-credit-actions-inline">
            <label className="stack cal-credit-select">
              <span className="small muted">Apply an existing block credit</span>
              <select
                disabled={busy || blocks.length === 0}
                value={selectedBlock}
                onChange={(event) => setSelectedBlock(event.target.value)}
              >
                {blocks.map((block) => (
                  <option key={block.lesson_block_id} value={block.lesson_block_id}>
                    {block.service_name} · {block.available_credits} credit{block.available_credits === 1 ? "" : "s"}
                  </option>
                ))}
              </select>
            </label>
            <button
              className="quiet"
              type="button"
              disabled={busy || selectedBlock === ""}
              onClick={() => void applyCredit()}
            >
              Apply credit
            </button>
          </div>
        )}

        {!moneyOnly && manualUseMessages.map((message) => (
          <p className="small muted" key={message}>{message}</p>
        ))}
        {problem && (uncertain ? <ActionConfirmation variant="uncertain" title="Payment outcome needs checking" description={problem} details={[{ value: "Check the lesson payment and credits before retrying. Do not start another payment while this outcome is unresolved." }]} /> : <div className="error small" role="alert">{problem}</div>)}
      </div>
    );
  }

  return (
    <div className="change-lesson cal-drawer-operation">
      <h3>Record payment received</h3>
      <p className="small muted">
        This records payment already received. The Hub does not process a card payment.
      </p>
      {payment.payment_id !== null && payment.amount_due !== null && (
        <div className="cal-payment-actions">
          <button type="button" disabled={busy} onClick={() => void mark("card_terminal")}>Paid by card machine</button>
          <button type="button" disabled={busy} onClick={() => void mark("cash")}>Paid cash</button>
          <button type="button" disabled={busy} onClick={() => void mark("bank_transfer")}>Paid by bank transfer</button>
        </div>
      )}
      {payment.payment_id === null && (
        <p className="small muted">There is no pending Amelia payment record to mark paid.</p>
      )}

      {!moneyOnly && <>
      <label className="stack cal-credit-select">
        <span className="small muted">Apply an existing block credit</span>
        <select
          disabled={busy || blocks === null || blocks.length === 0}
          value={selectedBlock}
          onChange={(event) => setSelectedBlock(event.target.value)}
        >
          {(blocks ?? []).map((block) => (
            <option key={block.lesson_block_id} value={block.lesson_block_id}>
              {block.service_name} · {block.available_credits} credit{block.available_credits === 1 ? "" : "s"}
            </option>
          ))}
        </select>
      </label>
      {blocks !== null && blocks.length === 0 && !hasCreditEntitlement && (
        <p className="small muted">No existing credits are available.</p>
      )}
      {manualUseMessages.map((message) => (
        <p className="small muted" key={message}>{message}</p>
      ))}
      <button
        className="quiet"
        type="button"
        disabled={busy || selectedBlock === ""}
        onClick={() => void applyCredit()}
      >
        Apply credit
      </button>
      </>}
      {problem && (uncertain ? <ActionConfirmation variant="uncertain" title="Payment outcome needs checking" description={problem} details={[{ value: "Check the lesson payment and credits before retrying. Do not start another payment while this outcome is unresolved." }]} /> : <div className="error small" role="alert">{problem}</div>)}
    </div>
  );
}
